import React, { useEffect, useState } from 'react'
import ky from '../Modules/ApiClient'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faAngleRight, faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { API_URL } from '../Globals'

type Outcome = 'SUCCESS' | 'FAILED' | 'BLOCKED'

interface LoginEvent {
  _id: string
  username: string
  outcome: Outcome
  createdAt: string
}

interface LoginHistoryResponse {
  items: LoginEvent[]
  totalCount: number
  page: number
  perPage: number
}

const outcomeLabels: Record<Outcome, string> = {
  SUCCESS: 'Onnistui',
  FAILED: 'Epäonnistui',
  BLOCKED: 'Estetty (lukitus)'
}

const outcomeStyles: Record<Outcome, string> = {
  SUCCESS: 'bg-emerald-50 text-emerald-700',
  FAILED: 'bg-red-50 text-red-700',
  BLOCKED: 'bg-amber-50 text-amber-800'
}

const formatDate = (value: string): string => {
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? 'Tuntematon ajankohta'
    : date.toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' })
}

interface Props {
  userId: string
  sessionToken?: string
}

const LoginHistory: React.FC<Props> = ({ userId, sessionToken }) => {
  const [scope, setScope] = useState<'user' | 'all'>('user')
  const [page, setPage] = useState(1)
  const [refresh, setRefresh] = useState(0)
  const [data, setData] = useState<LoginHistoryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    const path = scope === 'user' ? `/api/user/${userId}/login-events` : '/api/user/login-events'
    setLoading(true)
    setError('')
    setData(null)

    ky.get(`${API_URL}${path}`, {
      headers: {
        Authorization: `Bearer ${sessionToken || ''}`,
        Token: localStorage.getItem('token') || ''
      },
      searchParams: { page, perPage: 20 },
      signal: controller.signal
    }).json<LoginHistoryResponse>()
      .then(result => {
        if (!controller.signal.aborted) setData(result)
      })
      .catch(() => {
        if (!controller.signal.aborted) setError('Kirjautumishistoriaa ei voitu ladata.')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [scope, page, refresh, userId, sessionToken])

  const selectScope = (nextScope: 'user' | 'all') => {
    setScope(nextScope)
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil((data?.totalCount || 0) / 20))

  return (
    <section className="mt-4" aria-label="Kirjautumishistoria">
      <p className="mb-3 text-sm leading-relaxed text-gray-500">
        Onnistuneet, epäonnistuneet ja lukituksen estämät kirjautumiset.
        Historiatiedot säilyvät noin 90 päivää. Aiemmat kirjautumiset eivät näy takautuvasti.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Kirjautumishistorian rajaus" className="flex min-w-0 flex-1 rounded-xl bg-gray-100 p-1">
          <button type="button" onClick={() => selectScope('user')} aria-pressed={scope === 'user'}
            className={`min-h-10 min-w-0 flex-1 rounded-lg px-2 text-sm font-semibold ${scope === 'user' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}>
            Tämä käyttäjä
          </button>
          <button type="button" onClick={() => selectScope('all')} aria-pressed={scope === 'all'}
            className={`min-h-10 min-w-0 flex-1 rounded-lg px-2 text-sm font-semibold ${scope === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}>
            Kaikki yritykset
          </button>
        </div>
        <button type="button" onClick={() => setRefresh(value => value + 1)}
          aria-label="Päivitä kirjautumishistoria" title="Päivitä"
          className="flex h-11 w-11 flex-none items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">
          <FontAwesomeIcon icon={faRotate} />
        </button>
      </div>

      {loading ? (
        <p role="status" className="flex items-center justify-center gap-2 py-12 text-sm text-blue-700">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />Ladataan kirjautumisia…
        </p>
      ) : error ? (
        <p role="alert" className="mt-3 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>
      ) : !data || data.items.length === 0 ? (
        <p className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
          Ei kirjautumistapahtumia tällä rajauksella.
        </p>
      ) : (
        <>
          <p className="my-3 text-xs font-semibold text-gray-500">
            {data.totalCount.toLocaleString('fi-FI')} kirjautumistapahtumaa
          </p>
          <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
            {data.items.map(item => (
              <div key={item._id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-sm">
                <span className={`rounded-md px-2 py-1 text-xs font-bold ${outcomeStyles[item.outcome] || 'bg-gray-100 text-gray-700'}`}>
                  {outcomeLabels[item.outcome] || 'Tuntematon'}
                </span>
                <span className="min-w-0 flex-1 break-all font-medium text-gray-800">{item.username || 'Tuntematon käyttäjänimi'}</span>
                <time className="whitespace-nowrap text-xs tabular-nums text-gray-500" dateTime={item.createdAt}>
                  {formatDate(item.createdAt)}
                </time>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <nav className="mt-3 flex items-center justify-center gap-3" aria-label="Kirjautumishistorian sivutus">
              <button type="button" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}
                aria-label="Edellinen sivu" className="h-10 w-10 rounded-lg border border-gray-200 disabled:opacity-40">
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-xs font-semibold tabular-nums text-gray-600">{page} / {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}
                aria-label="Seuraava sivu" className="h-10 w-10 rounded-lg border border-gray-200 disabled:opacity-40">
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </nav>
          )}
        </>
      )}
    </section>
  )
}

export default LoginHistory
